import React, { useState } from "react";
import { RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import * as api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { FeatureLevelControl } from "./FeatureLevelControl";
import { featureLevel, withFeatureLevel, groupFeatures } from "../../lib/permissions";

const sameSet = (a = [], b = []) => a.length === b.length && [...a].sort().join() === [...b].sort().join();

// Matrice Rôles × Fonctionnalités (modifiable par l'admin, Administrateur = tout)
export function PermissionMatrix() {
    const { permCatalog, refreshPermCatalog } = useApp();
    const [saving, setSaving] = useState(null);
    if (!permCatalog) return null;

    const { features, roles, role_permissions: rolePerms, default_role_permissions: defaults } = permCatalog;
    const groups = groupFeatures(features);

    const save = async (role, perms) => {
        setSaving(role);
        try {
            await api.updateRolePermissions(role, perms);
            await refreshPermCatalog();
            toast.success("Plan des rôles mis à jour");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
        } finally { setSaving(null); }
    };

    const reset = async (role) => {
        setSaving(role);
        try {
            await api.resetRolePermissions(role);
            await refreshPermCatalog();
            toast.success("Valeurs par défaut restaurées");
        } catch { toast.error("Erreur"); }
        finally { setSaving(null); }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-testid="permission-matrix">
            <table className="w-full text-sm">
                <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-3 font-semibold text-gray-700 w-[34%]">Fonctionnalité</th>
                        {roles.map((r) => (
                            <th key={r.key} className="px-3 py-3 text-center font-semibold text-gray-700" data-testid={`matrix-role-${r.key}`}>
                                <div className="flex flex-col items-center gap-1">
                                    <span>{r.label}</span>
                                    {r.editable ? (
                                        !sameSet(rolePerms[r.key], defaults[r.key]) && (
                                            <button onClick={() => reset(r.key)} disabled={saving === r.key} className="text-[10px] font-normal text-gray-400 hover:text-gray-700 inline-flex items-center gap-0.5" data-testid={`matrix-reset-${r.key}`}>
                                                <RotateCcw className="w-2.5 h-2.5" />défaut
                                            </button>
                                        )
                                    ) : (
                                        <span className="text-[10px] font-normal text-gray-400 inline-flex items-center gap-0.5"><ShieldCheck className="w-2.5 h-2.5" />tout</span>
                                    )}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {groups.map((g) => (
                        <React.Fragment key={g.name}>
                            <tr className="bg-gray-50/60">
                                <td colSpan={roles.length + 1} className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">{g.name}</td>
                            </tr>
                            {g.features.map((f) => (
                                <tr key={f.key} className="border-t border-gray-100" data-testid={`matrix-row-${f.key}`}>
                                    <td className="px-4 py-2.5 align-top">
                                        <div className="font-medium text-gray-900">{f.label}</div>
                                        <div className="text-[11px] text-gray-500 leading-snug mt-0.5">{f.description}</div>
                                    </td>
                                    {roles.map((r) => (
                                        <td key={r.key} className="px-3 py-2.5 text-center align-middle">
                                            <FeatureLevelControl
                                                feature={f}
                                                value={r.editable ? featureLevel(rolePerms[r.key] || [], f.key) : null}
                                                inherited={r.editable ? null : f.levels[f.levels.length - 1]}
                                                disabled={!r.editable || saving === r.key}
                                                onChange={(lvl) => save(r.key, withFeatureLevel(rolePerms[r.key] || [], f.key, lvl))}
                                                testId={`matrix-${r.key}-${f.key}`}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
